import { useCandidateWhatsApp } from '@/hooks/use-candidate-whatsapp'
import { useWhatsAppTemplates } from '@/hooks/use-whatsapp-templates'
import { usePrivateChannel } from '@/lib/echo/use-private-channel'
import type { CandidateModel } from '@/ui'
import {
	broadcastChannel,
	useWhatsAppAccountShow,
	whatsAppAiAgentEvent,
	whatsAppAiReplyShowQueryKey,
} from '@jobilla/nexus-sdk'
import { CheckCheckIcon, MessageCircleIcon } from 'lucide-react'
import { AnimatePresence } from 'motion/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ConversationWindowBanner } from './conversation-window-banner'
import { mapApiMessageToWhatsAppMessage, mapApiTemplateToWhatsAppTemplate } from './types'
import type { WhatsAppMessage, WhatsAppTemplate } from './types'
import { useWhatsAppAiState } from './use-whatsapp-ai-state'
import { WhatsAppAiReviewPanel } from './whatsapp-ai-review-panel'
import { WhatsAppAiThinkingBubble } from './whatsapp-ai-thinking-bubble'
import { WhatsAppComposerBar } from './whatsapp-composer-bar'
import { WhatsAppItem } from './whatsapp-item'
import { WhatsAppTemplateSheet } from './whatsapp-template-sheet'

export type WhatsAppThreadProps = {
	// Only the id is read — lets callers without a full CandidateModel (e.g. the inbox) mount the thread.
	candidate: Pick<CandidateModel, 'id'>
	campaignId?: string
	organisationId: string
	isActive?: boolean
	/** Attributed as `source` on message-sent analytics. */
	trackingSource?: 'inbox' | 'candidate_card'
}

export function WhatsAppThread({
	candidate,
	campaignId,
	organisationId,
	isActive = true,
	trackingSource = 'candidate_card',
}: WhatsAppThreadProps) {
	const {
		messages: apiMessages,
		conversationWindow,
		sendTemplateMessage,
		sendTextMessage,
		markAsRead,
		markAllAsRead,
	} = useCandidateWhatsApp(candidate.id, campaignId, trackingSource)

	const { templates: apiTemplates } = useWhatsAppTemplates(organisationId, { enabled: isActive })
	const { data: whatsAppAccount } = useWhatsAppAccountShow(organisationId, { enabled: isActive })

	const aiState = useWhatsAppAiState(candidate.id, isActive)

	usePrivateChannel(
		isActive ? broadcastChannel.OrganisationWhatsAppAiAgent({ organisation: organisationId }) : null,
		{
			[whatsAppAiAgentEvent.ActivityChanged]: [whatsAppAiReplyShowQueryKey(candidate.id)],
		},
	)

	const messages: WhatsAppMessage[] = useMemo(
		() =>
			apiMessages
				.map((m) => mapApiMessageToWhatsAppMessage(m, candidate.id))
				.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()),
		[apiMessages, candidate.id],
	)

	const templates: WhatsAppTemplate[] = useMemo(
		() => apiTemplates.map(mapApiTemplateToWhatsAppTemplate),
		[apiTemplates],
	)

	const [isTemplateSheetOpen, setIsTemplateSheetOpen] = useState(false)

	const handleMessageClick = useCallback((messageId: string) => {
		const message = messages.find((m) => m.id === messageId)
		if (!message) {
			return
		}

		if (!message.isRead && message.isFromCandidate) {
			void markAsRead([messageId])
		}
	}, [messages, markAsRead])

	const handleSendTemplateMessage = useCallback(
		async (templateId: string, parameters: string[]) => {
			await sendTemplateMessage(templateId, parameters.length > 0 ? parameters : undefined)
			setIsTemplateSheetOpen(false)
		},
		[sendTemplateMessage],
	)

	// Adapts the hook's Promise<StoreResponse> to the composer's Promise<void> contract.
	const handleSendTextMessage = useCallback(
		async (content: string, mediaFile?: File) => {
			await sendTextMessage(content, mediaFile)
		},
		[sendTextMessage],
	)

	const unreadCount = useMemo(
		() => messages.filter((m) => !m.isRead && m.isFromCandidate).length,
		[messages],
	)

	// Keep the conversation pinned to the newest message, like any chat app.
	const listRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const el = listRef.current
		if (el) {
			el.scrollTop = el.scrollHeight
		}
	}, [messages.length, aiState?.activityState, aiState?.summary])

	return (
		<div className="flex h-full flex-col bg-white">
			<div className="flex flex-shrink-0 items-center justify-between border-b border-grey-200 bg-grey-50/50 px-5 py-3">
				<div className="flex items-center gap-3">
					<div className="flex items-center gap-2">
						<div className="flex size-8 items-center justify-center rounded-full bg-green-100">
							<MessageCircleIcon className="size-4 text-green-600" />
						</div>
						<div className="flex flex-col">
							<h2 className="font-semibold text-grey-900">WhatsApp</h2>
							{(whatsAppAccount?.phoneNumber || whatsAppAccount?.displayName) && (
								<span className="text-xs text-grey-500">
									{[whatsAppAccount.phoneNumber, whatsAppAccount.displayName]
										.filter(Boolean)
										.join(' · ')}
								</span>
							)}
						</div>
					</div>
					{unreadCount > 0 && (
						<span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">
							{unreadCount} new
						</span>
					)}
				</div>
				<button
					type="button"
					onClick={() => void markAllAsRead()}
					disabled={unreadCount === 0}
					className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-grey-600 transition-colors hover:bg-grey-100 hover:text-grey-900 disabled:cursor-not-allowed disabled:opacity-40"
				>
					<CheckCheckIcon className="size-4" />
					<span className="hidden sm:inline">{unreadCount === 0 ? 'All read' : 'Mark as read'}</span>
				</button>
			</div>

			<ConversationWindowBanner window={conversationWindow} />

			<div ref={listRef} className="min-h-0 flex-1 overflow-auto">
				{messages.length === 0
					? (
						<div className="flex h-full flex-col items-center justify-center p-8 text-grey-400">
							<MessageCircleIcon className="mb-3 size-12 opacity-50" />
							<p className="text-sm">No messages yet</p>
							<p className="mt-1 text-xs">Send a template message to start the conversation</p>
						</div>
					)
					: (
						<div className="py-2">
							{messages.map((message) => (
								<WhatsAppItem
									key={message.id}
									message={message}
									onClick={handleMessageClick}
								/>
							))}
							<WhatsAppAiThinkingBubble
								activityState={aiState?.activityState}
								activitySince={aiState?.activitySince}
							/>
						</div>
					)}
			</div>

			<div className="flex-shrink-0">
				<AnimatePresence>
					{isTemplateSheetOpen && (
						<WhatsAppTemplateSheet
							candidateId={candidate.id}
							templates={templates}
							onSendTemplate={handleSendTemplateMessage}
							onClose={() => setIsTemplateSheetOpen(false)}
						/>
					)}
				</AnimatePresence>
				<WhatsAppAiReviewPanel state={aiState} />
				<WhatsAppComposerBar
					conversationWindow={conversationWindow}
					onSendText={handleSendTextMessage}
					onOpenTemplates={() => setIsTemplateSheetOpen(true)}
				/>
			</div>
		</div>
	)
}
